import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Building2, Users, Package, BarChart2, PenLine,
  Gauge, Settings, BookOpen, TrendingUp,
  AlertTriangle, DollarSign, MessageSquare, RefreshCw
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import { theme } from '../styles/theme';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, CardHeader, CardTitle, Grid, Flex, Button,
  StatCard, StatValue, StatLabel,
  Spinner, LoadingContainer, EmptyState, EmptyIcon, EmptyText, EmptyHint,
  Badge
} from '../components/ui';
import { formatCurrency, currencyLocale } from '../utils/currency';
import api from '../services/api';

const fmt = (v, cur = 'BRL') => formatCurrency(v, cur, currencyLocale(cur));

export default function SallusDashboard() {
  const { t, i18n } = useTranslation('tenants');
  const tD = (k, opts) => t(`dashboard.${k}`, opts);
  const nav     = useNavigate();
  const [tenants,  setTenants]  = useState([]);
  const [sStats,   setSStats]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('tenants'); // tenants | risk | top

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantsRes, statsRes] = await Promise.all([
        api.get('/tenants'),
        api.get('/sallus/stats').catch(() => ({ data: null }))
      ]);
      setTenants(tenantsRes.data.tenants || []);
      setSStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const TOOLS = [
    { icon: Package,   label: t('toolMarketplace'), desc: t('libbenTools'), path: '/sallus/marketplace' },
    { icon: BarChart2, label: t('toolAnalytics'),   desc: t('libbenTools'), path: '/sallus/analytics'  },
    { icon: MessageSquare, label: t('toolWhatsappTemplates', { defaultValue: 'Templates WhatsApp' }), desc: t('toolWhatsappTemplatesDesc', { defaultValue: 'Templates de mensagem da plataforma' }), path: '/mktpl/sallus' },
    { icon: Settings,  label: t('title', { ns: 'settings' }), desc: t('settingsDesc'), path: '/sallus/settings'   },
    { icon: BookOpen,  label: t('helpManual'), desc: t('helpManualDesc'), path: '/sallus/help'       },
  ];

  const tierColor = { starter: theme.colors.textMuted, growth: '#3b82f6', pro: '#8b5cf6', enterprise: theme.colors.warning, trial: theme.colors.success };

  return (
    <>
      <AppHeader title="Salus Admin" />
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{tD('title')}</PageTitle>
            <PageSubtitle>{tD('subtitle')}</PageSubtitle>
          </div>
          <Flex gap={theme.spacing[2]}>
            <Button variant="secondary" size="sm" onClick={load} disabled={loading}>
              <RefreshCw size={14} />
            </Button>
            <Button onClick={() => nav('/sallus/tenants/new')}>
              <Building2 size={16} /> {t('newClient')}
            </Button>
          </Flex>
        </PageHeader>

        {loading ? (
          <LoadingContainer><Spinner /><span>{t('loading', { ns: 'common' })}</span></LoadingContainer>
        ) : (
          <>
            {/* Stats row */}
            <StatsRow>
              <StatCard accent={theme.colors.primary}>
                <Flex gap={theme.spacing[3]}>
                  <StatIconWrap $bg={theme.colors.primaryLight} $color={theme.colors.primary}><Building2 size={20} strokeWidth={1.5} /></StatIconWrap>
                  <div><StatValue>{sStats?.totals?.tenants ?? tenants.length}</StatValue><StatLabel>{tD('tenantsTotal')}</StatLabel></div>
                </Flex>
              </StatCard>
              <StatCard accent={theme.colors.success}>
                <Flex gap={theme.spacing[3]}>
                  <StatIconWrap $bg={theme.colors.successLight} $color={theme.colors.success}><Users size={20} strokeWidth={1.5} /></StatIconWrap>
                  <div><StatValue>{sStats?.totals?.active ?? 0}</StatValue><StatLabel>{tD('active')}</StatLabel></div>
                </Flex>
              </StatCard>
              <StatCard accent={theme.colors.success}>
                <Flex gap={theme.spacing[3]}>
                  <StatIconWrap $bg="#d1fae5" $color={theme.colors.success}><TrendingUp size={20} strokeWidth={1.5} /></StatIconWrap>
                  <div>
                    <StatValue>
                      {sStats?.mrrByCurrency?.BRL ? fmt(sStats.mrrByCurrency.BRL, 'BRL') : 'R$ 0'}
                    </StatValue>
                    <StatLabel>{tD('mrrBRL')}</StatLabel>
                  </div>
                </Flex>
              </StatCard>
              <StatCard accent="#8b5cf6">
                <Flex gap={theme.spacing[3]}>
                  <StatIconWrap $bg="${theme.colors.purpleLight}" $color="#8b5cf6"><MessageSquare size={20} strokeWidth={1.5} /></StatIconWrap>
                  <div><StatValue>{(sStats?.usage?.totalMessages || 0).toLocaleString(i18n.language)}</StatValue><StatLabel>{tD('msgsMonth')}</StatLabel></div>
                </Flex>
              </StatCard>
              {/* Card de mensagens de marketing */}
              {sStats?.usage?.marketingStats && (() => {
                const mkt = sStats.usage.marketingStats;
                const total = (mkt.whatsapp.marketing || 0) + (mkt.whatsapp.utility || 0) + (mkt.email.marketing || 0) + (mkt.email.utility || 0);
                return total > 0 ? (
                  <StatCard accent="${theme.colors.channels.whatsapp}">
                    <Flex gap={theme.spacing[3]}>
                      <StatIconWrap $bg="#dcfce7" $color="${theme.colors.channels.whatsapp}"><MessageSquare size={20} strokeWidth={1.5} /></StatIconWrap>
                      <div>
                        <StatValue>{total.toLocaleString(i18n.language)}</StatValue>
                        <StatLabel>{tD('mktMsgsMonth')}</StatLabel>
                        <MktBreakdown>
                          <span>📱 {(mkt.whatsapp.marketing + mkt.whatsapp.utility).toLocaleString()}</span>
                          <span>📧 {(mkt.email.marketing + mkt.email.utility).toLocaleString()}</span>
                        </MktBreakdown>
                      </div>
                    </Flex>
                  </StatCard>
                ) : null;
              })()}
              <StatCard accent={theme.colors.warning}>
                <Flex gap={theme.spacing[3]}>
                  <StatIconWrap $bg="#fef3c7" $color={theme.colors.warning}><DollarSign size={20} strokeWidth={1.5} /></StatIconWrap>
                  <div><StatValue>${(sStats?.usage?.totalCostUSD || 0).toFixed(2)}</StatValue><StatLabel>{tD('awsCost')}</StatLabel></div>
                </Flex>
              </StatCard>
              {(sStats?.atRisk?.length > 0) && (
                <StatCard accent={theme.colors.danger}>
                  <Flex gap={theme.spacing[3]}>
                    <StatIconWrap $bg={theme.colors.dangerLight} $color={theme.colors.danger}><AlertTriangle size={20} strokeWidth={1.5} /></StatIconWrap>
                    <div><StatValue>{sStats.atRisk.length}</StatValue><StatLabel>{tD('atRisk')}</StatLabel></div>
                  </Flex>
                </StatCard>
              )}
            </StatsRow>

            {/* Crescimento e aquisição */}
            {sStats?.growth && (
              <Card style={{ marginBottom: theme.spacing[6] }}>
                <CardHeader>
                  <CardTitle>{tD('acquisitionTitle', { defaultValue: 'Aquisição e Crescimento' })}</CardTitle>
                  <span style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>{tD('currentMonth', { defaultValue: 'Mês atual' })}</span>
                </CardHeader>
                <StatsRow style={{ marginBottom: 0 }}>
                  <StatCard accent={theme.colors.primary}>
                    <Flex gap={theme.spacing[3]}>
                      <StatIconWrap $bg={theme.colors.primaryLight} $color={theme.colors.primary}><TrendingUp size={20} strokeWidth={1.5} /></StatIconWrap>
                      <div><StatValue>{sStats.growth.newThisMonth}</StatValue><StatLabel>{tD('newTenantsMonth', { defaultValue: 'Novos tenants' })}</StatLabel></div>
                    </Flex>
                  </StatCard>
                  <StatCard accent={sStats.growth.growthMoM >= 0 ? theme.colors.success : theme.colors.danger}>
                    <Flex gap={theme.spacing[3]}>
                      <StatIconWrap $bg={sStats.growth.growthMoM >= 0 ? theme.colors.successLight : theme.colors.dangerLight} $color={sStats.growth.growthMoM >= 0 ? theme.colors.success : theme.colors.danger}><TrendingUp size={20} strokeWidth={1.5} /></StatIconWrap>
                      <div>
                        <StatValue>{sStats.growth.growthMoM !== null ? `${sStats.growth.growthMoM > 0 ? '+' : ''}${sStats.growth.growthMoM}%` : '—'}</StatValue>
                        <StatLabel>{tD('growthMoM', { defaultValue: 'Crescimento MoM' })}</StatLabel>
                      </div>
                    </Flex>
                  </StatCard>
                  {Object.entries(sStats.growth.bySource || {}).map(([src, count]) => (
                    <StatCard key={src} accent="#8b5cf6">
                      <Flex gap={theme.spacing[3]}>
                        <StatIconWrap $bg="${theme.colors.purpleLight}" $color="#8b5cf6"><Users size={20} strokeWidth={1.5} /></StatIconWrap>
                        <div>
                          <StatValue>{count}</StatValue>
                          <StatLabel>{src === 'referral' ? tD('sourceReferral', { defaultValue: 'Via indicação' }) : tD('sourceOrganic', { defaultValue: 'Orgânico' })}</StatLabel>
                        </div>
                      </Flex>
                    </StatCard>
                  ))}
                  {sStats.overagePctOfMrr !== undefined && (
                    <StatCard accent={theme.colors.warning}>
                      <Flex gap={theme.spacing[3]}>
                        <StatIconWrap $bg="#fef3c7" $color={theme.colors.warning}><DollarSign size={20} strokeWidth={1.5} /></StatIconWrap>
                        <div><StatValue>{sStats.overagePctOfMrr}%</StatValue><StatLabel>{tD('overagePct', { defaultValue: 'Overage % MRR' })}</StatLabel></div>
                      </Flex>
                    </StatCard>
                  )}
                </StatsRow>
              </Card>
            )}

            {/* ARPU por tier */}
            {sStats?.arpu && Object.keys(sStats.arpu).length > 0 && (
              <Card style={{ marginBottom: theme.spacing[6] }}>
                <CardHeader>
                  <CardTitle>{tD('arpuTitle', { defaultValue: 'ARPU por Tier (BRL)' })}</CardTitle>
                </CardHeader>
                <StatsRow style={{ marginBottom: 0 }}>
                  {Object.entries(sStats.arpu).map(([tier, arpu]) => (
                    <StatCard key={tier} accent={tierColor[tier] || theme.colors.textMuted}>
                      <StatValue>{fmt(arpu, 'BRL')}</StatValue>
                      <StatLabel style={{ textTransform: 'capitalize' }}>{tier}</StatLabel>
                    </StatCard>
                  ))}
                </StatsRow>
              </Card>
            )}

            {/* Distribuição de tiers */}
            {sStats?.byTier && Object.keys(sStats.byTier).length > 0 && (
              <Card style={{ marginBottom: theme.spacing[6] }}>
                <CardHeader>
                  <CardTitle>{tD('tierDistribution')}</CardTitle>
                  <span style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>
                    {tD('tenantsActiveTrialCount', { count: (sStats.totals?.active || 0) + (sStats.totals?.trial || 0) })}
                  </span>
                </CardHeader>
                <TierBar>
                  {Object.entries(sStats.byTier).map(([tier, count]) => {
                    const total = Object.values(sStats.byTier).reduce((a, b) => a + b, 0);
                    const pct   = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <TierSegment key={tier} $pct={pct} $color={tierColor[tier] || theme.colors.textMuted} title={`${tier}: ${count} (${pct}%)`} />
                    );
                  })}
                </TierBar>
                <TierLegend>
                  {Object.entries(sStats.byTier).map(([tier, count]) => (
                    <TierLegendItem key={tier}>
                      <TierDot $color={tierColor[tier] || theme.colors.textMuted} />
                      <span style={{ textTransform: 'capitalize' }}>{tier}</span>
                      <TierCount>{count}</TierCount>
                    </TierLegendItem>
                  ))}
                </TierLegend>
              </Card>
            )}

            {/* Tabs */}
            <TabBar>
              {[
                { key: 'tenants', label: tD('tabAll', { count: tenants.length }) },
                { key: 'top',     label: tD('tabTop', { count: sStats?.topTenants?.length || 0 }) },
                { key: 'risk',    label: tD('tabRisk', { count: sStats?.atRisk?.length || 0 }), warn: (sStats?.atRisk?.length || 0) > 0 },
              ].map(tb => (
                <TabBtn key={tb.key} $active={tab === tb.key} $warn={tb.warn} onClick={() => setTab(tb.key)}>
                  {tb.label}
                </TabBtn>
              ))}
            </TabBar>

            {/* Tab: Todos os tenants */}
            {tab === 'tenants' && (
              <Card padding={false} style={{ marginBottom: theme.spacing[6] }}>
                {tenants.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon><Building2 size={40} strokeWidth={1} /></EmptyIcon>
                    <EmptyText>{t('noTenants')}</EmptyText>
                    <EmptyHint>{t('noTenantsHint')}</EmptyHint>
                  </EmptyState>
                ) : (
                  <TenantTable>
                    {tenants.map(tenant => (
                      <TenantRow key={tenant.tenantId}>
                        <TenantMain>
                          <TenantAvatar>{tenant.name?.charAt(0)?.toUpperCase()}</TenantAvatar>
                          <div>
                            <TenantName>{tenant.name}</TenantName>
                            <TenantMeta>{tenant.tenantId} · {tenant.email}</TenantMeta>
                          </div>
                        </TenantMain>
                        <TenantMeta2>
                          <Badge variant={
                            tenant.status === 'active' ? 'success' :
                            tenant.status === 'trial'  ? 'info' :
                            tenant.status === 'suspended' ? 'danger' : 'neutral'
                          }>
                            {tenant.status}
                          </Badge>
                          <TierPill $color={tierColor[tenant.tier] || theme.colors.textMuted}>{tenant.tier}</TierPill>
                          <MetaChip>{tenant.currency || 'BRL'}</MetaChip>
                        </TenantMeta2>
                        <TenantActions>
                          <Button variant="ghost" size="sm" onClick={() => nav(`/dashboard/${tenant.tenantId}`)}>
                            <Gauge size={14} /> {t('actionDashboard')}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => nav(`/sallus/tenants/edit/${tenant.tenantId}`)}>
                            <PenLine size={14} /> {t('actionEdit')}
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => nav(`/billing/${tenant.tenantId}`)}>
                            <DollarSign size={14} /> {t('actionBilling')}
                          </Button>
                        </TenantActions>
                      </TenantRow>
                    ))}
                  </TenantTable>
                )}
              </Card>
            )}

            {/* Tab: Top uso */}
            {tab === 'top' && (
              <Card padding={false} style={{ marginBottom: theme.spacing[6] }}>
                {!sStats?.topTenants?.length ? (
                  <EmptyState><EmptyText>{tD('noUsageData')}</EmptyText></EmptyState>
                ) : (
                  <UsageTable>
                    <thead>
                      <tr>
                        <Th>{tD('colRank')}</Th><Th>{tD('colTenant')}</Th><Th>{tD('colTier')}</Th>
                        <Th align="right">{tD('colConversations')}</Th>
                        <Th align="right">{tD('colMessages')}</Th>
                        <Th align="right">{tD('colAwsCost')}</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {sStats.topTenants.map((t, i) => (
                        <tr key={t.tenantId} style={{ cursor: 'pointer' }} onClick={() => nav(`/billing/${t.tenantId}`)}>
                          <Td><RankNum>{i + 1}</RankNum></Td>
                          <Td><strong>{t.name}</strong><br /><small style={{ color: theme.colors.textMuted }}>{t.tenantId}</small></Td>
                          <Td><TierPill $color={tierColor[t.tier] || theme.colors.textMuted}>{t.tier}</TierPill></Td>
                          <Td>{t.conversations.toLocaleString(i18n.language)}</Td>
                          <Td align="right">{t.messages.toLocaleString(i18n.language)}</Td>
                          <Td align="right">${t.costUSD.toFixed(4)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </UsageTable>
                )}
              </Card>
            )}

            {/* Tab: Em risco */}
            {tab === 'risk' && (
              <Card padding={false} style={{ marginBottom: theme.spacing[6] }}>
                {!sStats?.atRisk?.length ? (
                  <EmptyState><EmptyIcon>✅</EmptyIcon><EmptyText>{tD('noAtRisk')}</EmptyText></EmptyState>
                ) : (
                  <TenantTable>
                    {sStats.atRisk.map(t => (
                      <TenantRow key={t.tenantId}>
                        <TenantMain>
                          <TenantAvatar style={{ background: theme.colors.dangerLight, color: theme.colors.danger }}>
                            {t.name?.charAt(0)?.toUpperCase()}
                          </TenantAvatar>
                          <div>
                            <TenantName>{t.name}</TenantName>
                            <TenantMeta>{t.tenantId}</TenantMeta>
                          </div>
                        </TenantMain>
                        <TenantMeta2>
                          <Badge variant="danger">{t.status}</Badge>
                          <RiskReason>
                            {t.reason === 'payment_failed' ? `❌ ${tD('reasonPaymentFailed')}` :
                             t.reason === 'trial_payment_failed' ? `❌ ${tD('reasonTrialPaymentFailed')}` :
                             t.reason === 'trial_expiring' ? `⏰ ${tD('reasonTrialExpiring', { date: t.trialEndsAt ? new Date(t.trialEndsAt).toLocaleDateString(i18n.language) : '?' })}` :
                             t.reason}
                          </RiskReason>
                        </TenantMeta2>
                        <TenantActions>
                          <Button variant="secondary" size="sm" onClick={() => nav(`/billing/${t.tenantId}`)}>
                            <DollarSign size={14} /> {t('actionBilling')}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => nav(`/sallus/tenants/edit/${t.tenantId}`)}>
                            <PenLine size={14} /> {t('actionEdit')}
                          </Button>
                        </TenantActions>
                      </TenantRow>
                    ))}
                  </TenantTable>
                )}
              </Card>
            )}

            {/* Ferramentas */}
            <SectionLabel>{tD('tools')}</SectionLabel>
            <Grid min="200px" gap={theme.spacing[4]}>
              {TOOLS.map(({ icon: Icon, label, desc, path }) => (
                <ToolCard key={label} onClick={() => nav(path)}>
                  <ToolIconWrap><Icon size={22} strokeWidth={1.5} /></ToolIconWrap>
                  <ToolLabel>{label}</ToolLabel>
                  <ToolDesc>{desc}</ToolDesc>
                </ToolCard>
              ))}
            </Grid>
          </>
        )}
      </PageContainer>
    </>
  );
}

const StatsRow = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: ${theme.spacing[4]}; margin-bottom: ${theme.spacing[6]};`;
const StatIconWrap = styled.div`width: 44px; height: 44px; border-radius: ${theme.radii.lg}; background: ${p => p.$bg}; color: ${p => p.$color}; display: flex; align-items: center; justify-content: center; flex-shrink: 0;`;
const TierBar = styled.div`display: flex; height: 12px; border-radius: ${theme.radii.full}; overflow: hidden; margin: ${theme.spacing[3]} 0;`;
const TierSegment = styled.div`width: ${p => p.$pct}%; background: ${p => p.$color}; transition: width 0.3s;`;
const TierLegend = styled.div`display: flex; gap: ${theme.spacing[4]}; flex-wrap: wrap;`;
const TierLegendItem = styled.div`display: flex; align-items: center; gap: ${theme.spacing[1]}; font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;
const TierDot = styled.div`width: 8px; height: 8px; border-radius: 50%; background: ${p => p.$color};`;
const TierCount = styled.span`font-weight: 600; color: ${theme.colors.textPrimary};`;
const TierPill = styled.span`font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: ${theme.radii.full}; background: ${p => p.$color}22; color: ${p => p.$color}; text-transform: capitalize;`;
const TabBar = styled.div`display: flex; gap: ${theme.spacing[1]}; margin-bottom: ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.border}; padding-bottom: ${theme.spacing[1]};`;
const TabBtn = styled.button`padding: ${theme.spacing[2]} ${theme.spacing[4]}; font-size: ${theme.typography.sm}; font-weight: ${p => p.$active ? 600 : 400}; color: ${p => p.$active ? theme.colors.primary : p.$warn ? theme.colors.danger : theme.colors.textMuted}; background: none; border: none; border-bottom: 2px solid ${p => p.$active ? theme.colors.primary : 'transparent'}; cursor: pointer; margin-bottom: -1px;`;
const TenantTable = styled.div`display: flex; flex-direction: column;`;
const TenantRow = styled.div`display: flex; align-items: center; gap: ${theme.spacing[4]}; padding: ${theme.spacing[4]} ${theme.spacing[5]}; border-bottom: 1px solid ${theme.colors.border}; flex-wrap: wrap; &:last-child { border-bottom: none; } &:hover { background: ${theme.colors.gray50}; }`;
const TenantMain = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; flex: 1; min-width: 200px;`;
const TenantAvatar = styled.div`width: 36px; height: 36px; border-radius: ${theme.radii.md}; background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.bold}; display: flex; align-items: center; justify-content: center; flex-shrink: 0;`;
const TenantName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const TenantMeta = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const TenantMeta2 = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; flex-wrap: wrap;`;
const MetaChip = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const TenantActions = styled.div`display: flex; gap: ${theme.spacing[2]}; flex-wrap: wrap;`;
const RiskReason = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.danger};`;
const UsageTable = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`font-size: ${theme.typography.xs}; font-weight: 600; color: ${theme.colors.textMuted}; text-align: ${p => p.align || 'left'}; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.border};`;
const Td = styled.td`font-size: ${theme.typography.sm}; color: ${theme.colors.textPrimary}; text-align: ${p => p.align || 'left'}; padding: ${theme.spacing[3]} ${theme.spacing[4]}; border-bottom: 1px solid ${theme.colors.gray100}; &:hover { background: ${theme.colors.gray50}; }`;
const RankNum = styled.div`width: 24px; height: 24px; border-radius: 50%; background: ${theme.colors.gray100}; color: ${theme.colors.textMuted}; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center;`;
const SectionLabel = styled.h2`font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[4]};`;
const ToolCard = styled.button`display: flex; flex-direction: column; gap: ${theme.spacing[2]}; padding: ${theme.spacing[5]}; background: ${theme.colors.white}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.xl}; cursor: pointer; text-align: left; transition: ${theme.transitions.base}; &:hover { border-color: ${theme.colors.primary}; box-shadow: ${theme.shadows.md}; transform: translateY(-2px); }`;
const ToolIconWrap = styled.div`width: 40px; height: 40px; background: ${theme.colors.primaryLight}; color: ${theme.colors.primary}; border-radius: ${theme.radii.md}; display: flex; align-items: center; justify-content: center;`;
const ToolLabel = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const ToolDesc = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const MktBreakdown = styled.div`display: flex; gap: ${theme.spacing[3]}; font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: 2px;`;
